import classNames from 'clsx';
import PropTypes from 'prop-types';
import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { BOTTOM_BANNER_ENABLED, FREESTAR_ENABLED } from 'src/common/constants/experiments/ads';
import useActions from 'src/common/hooks/useActions';
import useExperiment from 'src/common/hooks/useExperiment';
import {
  selectBottomAdBannerHeight,
  selectIsMobile,
  selectRouteProps,
} from 'src/common/selectors/app';
import { selectIsDesktopNowPlayingEnabled } from 'src/common/selectors/config';
import { getDisplayAdAttributes, gptSlotNames } from 'src/common/utils/ads/config';
import { useDetermineAdEligibility } from 'src/common/utils/ads/determineAdEligibility';
import { useShouldShowPlayer } from 'src/common/utils/playerStatus/shouldShowPlayer';
import { setBottomAdBannerHeight } from '../../actions/app';
import styles from './bottom-banner.module.scss';
import DisplayAd from './DisplayAd';

export default function BottomBanner({ isNowPlayingBanner }) {
  const routeProps = useSelector(selectRouteProps);
  const canShowAds = useDetermineAdEligibility(routeProps.guideContext?.guideId);

  const bannerRef = useRef();
  const actions = useActions({ setBottomAdBannerHeight });

  const isPlayerOpen = useShouldShowPlayer();
  const bottomAdBannerHeight = useSelector(selectBottomAdBannerHeight);
  const isMobile = useSelector(selectIsMobile);
  const isDesktopNowPlayingEnabled = useSelector(selectIsDesktopNowPlayingEnabled);
  const isFreestarEnabled = useExperiment(FREESTAR_ENABLED);
  const isBottomBannerEnabled = useExperiment(BOTTOM_BANNER_ENABLED);
  const isNowPlayingBannerEnabled = useExperiment('ads.display.nowPlayingBottomBanner.enabled');

  const showDisplayAd =
    canShowAds &&
    !routeProps.hideAds &&
    isBottomBannerEnabled &&
    (!isNowPlayingBanner || isNowPlayingBannerEnabled);

  useEffect(() => {
    if (showDisplayAd) {
      const resizeObserver = new ResizeObserver(() => {
        if (bannerRef.current) {
          const height = bannerRef.current.offsetHeight || 0;
          actions.setBottomAdBannerHeight(height);
        }
      });

      if (bannerRef.current) {
        resizeObserver.observe(bannerRef.current);
      }

      return () => {
        actions.setBottomAdBannerHeight(0);
        resizeObserver.disconnect();
      };
    }
  }, [actions, showDisplayAd]);

  return !showDisplayAd ? null : (
    <div
      ref={bannerRef}
      className={classNames(styles.container, {
        [styles.withPlayer]: (isDesktopNowPlayingEnabled || !isNowPlayingBanner) && isPlayerOpen,
        // This is to prevent rendering bottom border when no content rendered yet
        [styles.withBorder]: (isNowPlayingBanner || isPlayerOpen) && bottomAdBannerHeight,
        [styles.mobile]: isMobile,
      })}
    >
      <DisplayAd
        guideId={routeProps.guideContext?.guideId}
        matchUrl={routeProps.matchUrl}
        {...getDisplayAdAttributes(
          isNowPlayingBanner ? gptSlotNames.nowPlaying_bottom_banner : gptSlotNames.bottom_banner,
          isFreestarEnabled,
        )}
      />
    </div>
  );
}

BottomBanner.propTypes = {
  isNowPlayingBanner: PropTypes.bool,
};
